import { useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LoadingContextType } from '../../../../context/loadingContext/LoadingContext';
import { QUERY_KEYS } from '../../../../queries/queryKeys';
import { useAvailableDatasetsQuery } from '../../../../queries/useBestDatasetQuery';
import useGetShoppingCartQuery from '../../../../queries/useGetShoppingCartQuery';
import useAnalyticsStore from '../../../../store/analyticsStore';
import useAuthStore from '../../../../store/authStore';
import useDatasetStore from '../../../../store/datasetStore';
import useHomeStore from '../../../../store/homeStore';
import useLegendStore from '../../../../store/legendStore';
import { useActiveMarker, useMarkerStore } from '../../../../store/markerStore';
import { IBestDataset } from '../../../../types/analytics';
import { DatasetType, IMarker } from '../../../../types/marker';
import { IDatasetBrief, KeyType } from '../../../../types/metadata';
import { IBestDataset as IBestDatasetRep } from '../../../../types/search';
import { pointIsInCart } from '../../../../utils/PointHelper';
import { SEARCH_PARAM } from '../../../../utils/common';
import { getNextFreeNumber } from '../../../../utils/getNexFreeNumber';
import { withLoading } from '../../../common/Loading/withLoading';
import { ResultPanelProps } from '../ResultPanel';
import { getPointNameExistsError } from '../component/Modal/Error/PointNameError';
import { isPointNameValid } from '../utils/pointNameValidation';

interface MarkerManagementProps {
  loadingContext: LoadingContextType;
  props: ResultPanelProps;
}

export const useMarkerManagement = ({
  loadingContext,
  props,
}: MarkerManagementProps) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const previewAnalytics = useAnalyticsStore((state) => state.previewAnalytics);
  const setPreviewAnalytics = useAnalyticsStore(
    (state) => state.setPreviewAnalytics
  );

  const overridePointData = useHomeStore((state) => state.overridePointData);
  const showMesh = useLegendStore((state) => state.showMesh);
  const setShowLayerView = useLegendStore((state) => state.setShowLayerView);
  // const legend = useLegendStore((state) => state.legend);
  const setLegend = useLegendStore((state) => state.setLegend);
  const setShowMesh = useLegendStore((state) => state.setShowMesh);
  const datasetBriefs = useDatasetStore((state) => state.datasetBriefs);
  const markers = useMarkerStore((state) => state.markers);
  const activeMarker = useActiveMarker();
  const updateMarker = useMarkerStore((state) => state.updateMarker);
  const deleteMarker = useMarkerStore((state) => state.deleteMarker);
  const setActiveMarker = useMarkerStore((state) => state.setActiveMarker);
  const setAddMarkerMode = useMarkerStore((state) => state.setAddMarkerMode);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  const [nextFreePointNumber, setNextFreePointNumber] = useState('');
  const [activePanelKey, setActivePanelKey] = useState<string | string[]>('');
  const [tempCoords, setTempCoords] = useState<number[] | null>(null);
  const [
    isChangeCoordinatesWarningVisible,
    setIsChangeCoordinatesWarningVisible,
  ] = useState(false);
  const [resetCoordsKey, setResetCoordsKey] = useState('');

  const { data } = useGetShoppingCartQuery();
  const shoppingItems = useMemo(
    () => data?.shoppingItems || {},
    [data?.shoppingItems]
  );

  const { bestDatasets, changeCoordinates } = useAvailableDatasetsQuery(
    activeMarker?.coordinates ?? [],
    true
  );

  const generateDatasetOptions = useCallback(
    (datasetBriefs: IDatasetBrief[], datasetIds: string[]): IBestDataset[] => {
      return datasetIds.reduce(
        (acc: IBestDataset[], id: string): IBestDataset[] => {
          const brief = datasetBriefs.find((b) => b.id === id);
          if (!brief) return acc;

          acc.push({
            id,
            label: brief.isFWE ? brief.title : brief.shortDescription,
            value: brief.acronym,
          });

          return acc;
        },
        []
      );
    },
    []
  );

  const updateDownloadPage = useCallback(
    (
      activeMarker: IMarker,
      bestDatasets: IBestDatasetRep,
      datasetDefaultParams: Record<'SW' | 'HD' | 'Wind', string>
    ) => {
      const updatedDownloadPage = activeMarker.downloadPage
        ? [...activeMarker.downloadPage]
        : [];

      const activePanelFromUrl = new URLSearchParams(location.search).get(
        SEARCH_PARAM.ACTIVE_PANEL
      );
      const preferredDatasetKey =
        activeMarker.activeDatasetKey ??
        (activePanelFromUrl === DatasetType.SW ||
        activePanelFromUrl === DatasetType.HD ||
        activePanelFromUrl === DatasetType.Wind
          ? activePanelFromUrl
          : undefined);

      for (const item of updatedDownloadPage) {
        item.options = generateDatasetOptions(
          datasetBriefs,
          bestDatasets[item.radioKey]
        );

        if (
          !overridePointData &&
          item.datasetId &&
          bestDatasets[item.radioKey].includes(item.datasetId)
        )
          continue;

        item.datasetId = bestDatasets[item.radioKey + '_Default'].toString();
        const paramId = datasetDefaultParams[item.radioKey as KeyType];
        if (!paramId) continue;

        const isFound = item.options.find((option) => option.id === paramId);
        if (!isFound) continue;

        item.datasetId = paramId;
      }
      const downloadPage =
        updatedDownloadPage.find(
          (item) => item.radioKey === preferredDatasetKey
        ) ?? updatedDownloadPage[0];

      return { downloadPage, updatedDownloadPage };
    },
    [datasetBriefs, generateDatasetOptions, overridePointData]
  );

  const getDefaultParams = useCallback(
    (coordinates: number[]): Record<'SW' | 'HD' | 'Wind', string> => {
      const searchParams = new URLSearchParams(location.search);
      searchParams.set(SEARCH_PARAM.COOR, JSON.stringify(coordinates));
      navigate(`/?${searchParams.toString()}`);

      return {
        SW: searchParams.get('SW') ?? '',
        HD: searchParams.get('HD') ?? '',
        Wind: searchParams.get('Wind') ?? '',
      };
    },
    [navigate]
  );

  const updateActivePoint = useCallback(
    (bestDatasets: IBestDatasetRep, coordinates: number[]) => {
      if (!activeMarker) return;

      const datasetDefaultParams = getDefaultParams(coordinates);

      const { updatedDownloadPage, downloadPage } = updateDownloadPage(
        activeMarker,
        bestDatasets,
        datasetDefaultParams
      );

      updateMarker(activeMarker.id, {
        coordinates: coordinates,
        allDataset: bestDatasets,
        downloadPage: updatedDownloadPage,
        activeDatasetKey:
          (downloadPage?.radioKey as DatasetType | undefined) ??
          activeMarker.activeDatasetKey,
        dataset: downloadPage?.datasetId ?? '',
      });

      props.onMeshControl(showMesh ? downloadPage?.datasetId ?? '' : '');

      props.onMark(false);
      setShowLayerView(true);
    },
    [
      activeMarker,
      props,
      // legend,
      // navigate,
      // datasetBriefs,
      // generateDatasetOptions,
      // overridePointData,
      showMesh,
      updateMarker,
      // setLegend,
      setShowLayerView,
      getDefaultParams,
      updateDownloadPage,
    ]
  );

  const onChangePointLocation = useCallback(
    async (coords: number[], forceChange?: boolean) => {
      setResetCoordsKey('');
      props.coordsMoved && props.onResetCoordsMove();
      // Before we change coordinates, check if preview analytics have been created already
      const usedInPreview = previewAnalytics.find(
        (pa) => pa.coordinates.join() === activeMarker?.coordinates.join()
      );
      if (usedInPreview !== undefined && !forceChange) {
        setTempCoords(coords);
        setIsChangeCoordinatesWarningVisible(true);
      } else {
        // If not used in preview, coordinates can be changed immediately but
        // check if datasets are available for given coordinates
        const bestDatasets = await changeCoordinates(coords, true);

        if (bestDatasets) {
          updateActivePoint(bestDatasets, coords);
        } else {
          // Trigger the CoordinateInfo component to reset the active point's coordinates
          !props.coordsMoved &&
            activeMarker &&
            setResetCoordsKey(activeMarker.label);
        }
      }
    },
    [
      activeMarker,
      changeCoordinates,
      previewAnalytics,
      props,
      updateActivePoint,
    ]
  );

  const onChangeActiveKey = useCallback(
    (key: string | string[]) => {
      props.setMeshUrl(undefined);
      const point = markers.find((x) => x.id === key);
      if (!point) {
        setActivePanelKey('');
        return;
      }
      const datasetIdParams = {
        SW: '',
        HD: '',
        Wind: '',
      };
      const updatedDownloadPage = [...point.downloadPage];
      updatedDownloadPage.forEach((item) => {
        datasetIdParams[item.radioKey as KeyType] = item.datasetId;
      });
      updateMarker(point.id, { downloadPage: updatedDownloadPage });

      const searchParams = new URLSearchParams(location.search);

      const coorEncoded = encodeURIComponent(
        JSON.stringify(point.coordinates)
      ).trim();
      const SWEncoded = encodeURIComponent(datasetIdParams.SW).trim();
      const HDEncoded = encodeURIComponent(datasetIdParams.HD).trim();
      const WindEncoded = encodeURIComponent(datasetIdParams.Wind).trim();
      searchParams.set(SEARCH_PARAM.COOR, coorEncoded);
      searchParams.set(SEARCH_PARAM.SW, SWEncoded);
      searchParams.set(SEARCH_PARAM.HD, HDEncoded);
      searchParams.set(SEARCH_PARAM.WIND, WindEncoded);
      if (point.fwePointName) {
        const fwePointNameEncoded = encodeURIComponent(
          point.fwePointName
        ).trim();
        searchParams.set(SEARCH_PARAM.FWEPOINTNAME, fwePointNameEncoded);
      }

      setActiveMarker(point.id);
      navigate(`/?${searchParams.toString()}`);
      setActivePanelKey(key);
    },
    [markers, navigate, setActiveMarker, updateMarker]
  );

  const onPointsDelete = useCallback(
    (id: string) => {
      const deletedMarker = markers.find((m) => m.id === id);
      if (deletedMarker) {
        if (id === activePanelKey) {
          navigate({ pathname: '/' }, { replace: false });
        }
        deletedMarker.downloadPage.forEach((item) => {
          queryClient.removeQueries({
            queryKey: [QUERY_KEYS.map.overlay.shapeFile, item.datasetId],
          });
          queryClient.removeQueries({
            queryKey: [QUERY_KEYS.map.overlay.surfaceMap, item.datasetId],
          });
        });
        props.setMeshUrl(undefined);
        setShowMesh(false);
        setLegend(null);
        deleteMarker(id);
      }
    },
    [
      activePanelKey,
      deleteMarker,
      markers,
      navigate,
      props,
      queryClient,
      setLegend,
      setShowMesh,
    ]
  );

  const onPointNameChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>, id: string) => {
      event.stopPropagation();
      const label = event.target.value;
      if (!isPointNameValid(label)) {
        return;
      }

      const selected = markers.find((m) => m.id === id);
      if (!selected) return;
      const exists = markers.find((m) => m.label === label && m.id !== id);
      if (exists) {
        message.error(getPointNameExistsError(label));
      } else {
        if (isAuthenticated() && Object.keys(shoppingItems).includes(label)) {
          message.error('This point name is already in the shopping cart');
        } else {
          updateMarker(id, { label: label });
        }
      }
    },
    [isAuthenticated, markers, shoppingItems, updateMarker]
  );

  const validateInput = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const label = event.target.value;
    event.stopPropagation();
    return isPointNameValid(label);
  }, []);

  const handleChangeCoordinatesWarningOk = useCallback(async () => {
    setIsChangeCoordinatesWarningVisible(false);
    if (tempCoords && activeMarker !== undefined) {
      const bestDatasets = await changeCoordinates(tempCoords, true);

      if (bestDatasets) {
        // before we change the coordinates, we need to release related previews from context
        const without = previewAnalytics.filter(
          (pa) => pa.coordinates.join() !== activeMarker?.coordinates.join()
        );
        setPreviewAnalytics(without);
        setTempCoords(null);
        updateActivePoint(bestDatasets, tempCoords);
      } else {
        // We need to trigger the CoordinateInfo component to reset the active point's coordinates
        activeMarker && setResetCoordsKey(activeMarker.label);
      }
    }
  }, [
    activeMarker,
    changeCoordinates,
    previewAnalytics,
    setPreviewAnalytics,
    tempCoords,
    updateActivePoint,
  ]);

  const handleChangeCoordinatesWarningCancel = useCallback(() => {
    setTempCoords(null);
    setIsChangeCoordinatesWarningVisible(false);
    activeMarker && setResetCoordsKey(activeMarker.label);
  }, [activeMarker]);

  const updateMeshValue = useCallback(
    async (value: string, showMesh: boolean) => {
      if (showMesh) {
        props.onMeshControl(value);
      }
    },
    []
  );

  useEffect(() => {
    if (!activeMarker || !activeMarker.downloadPage) return;
    if (!bestDatasets || !datasetBriefs) return;

    const datasetDefaultParams = getDefaultParams(activeMarker.coordinates);

    updateDownloadPage(activeMarker, bestDatasets, datasetDefaultParams);
  }, [bestDatasets, datasetBriefs, generateDatasetOptions, activeMarker]);

  useEffect(() => {
    if (props.isAddMarkerMode) {
      const next = getNextFreeNumber(markers, shoppingItems);
      setNextFreePointNumber(next);
      setActivePanelKey(next);
    } else {
      if (activeMarker) {
        setActivePanelKey(activeMarker.id);
      } else {
        setActivePanelKey('');
      }
    }
  }, [props.isAddMarkerMode, activeMarker, markers, shoppingItems]);

  const hasDatasets = useMemo(() => {
    if (!activeMarker || !activeMarker.downloadPage) return false;

    return (
      activeMarker.downloadPage.some((page) => page?.options?.length > 0) ??
      false
    );
  }, [activeMarker?.dataset, activeMarker?.downloadPage]);

  useEffect(() => {
    if (props.coordsMoved && activeMarker && activeMarker.coordinatesToMove) {
      const inCart = pointIsInCart(activeMarker, shoppingItems);
      if (inCart) {
        message.info('You cannot move a point that is already in the cart.');
        props.onResetCoordsMove();
      } else {
        onChangePointLocation(activeMarker.coordinatesToMove);
      }
    }
  }, [
    props.coordsMoved,
    props.onResetCoordsMove,
    activeMarker?.coordinatesToMove,
    shoppingItems,
  ]);

  useEffect(() => {
    const activeDataset = activeMarker?.downloadPage.find(
      (item) => item.radioKey === activeMarker?.activeDatasetKey
    );
    if (activeDataset) {
      withLoading(
        () => updateMeshValue(activeDataset.datasetId, showMesh),
        loadingContext,
        { source: 'meshValue' }
      );
    }
  }, [activeMarker?.downloadPage, activeMarker?.activeDatasetKey, showMesh]);

  useEffect(() => {
    if (bestDatasets && activeMarker?.coordinates) {
      updateActivePoint(bestDatasets, activeMarker.coordinates);
    }
  }, [bestDatasets, activeMarker?.coordinates]);

  return {
    activeMarker,
    nextFreePointNumber,
    activePanelKey,
    hasDatasets,
    markers,
    isChangeCoordinatesWarningVisible,
    resetCoordsKey,
    onChangePointLocation,
    onChangeActiveKey,
    onPointsDelete,
    onPointNameChange,
    validateInput,
    handleChangeCoordinatesWarningOk,
    handleChangeCoordinatesWarningCancel,
    setAddMarkerMode,
    updateMeshValue,
  };
};
